// CalendarView.jsx
import React, { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEvents } from '../../Redux/Actions/actions';
import styles from './CalendarView.module.css';
import 'react-calendar/dist/Calendar.css';

const CalendarView = () => {
  const dispatch = useDispatch();
  const eventos = useSelector(state => state.events);
  const [value, setValue] = useState(new Date());
  const [modalEventos, setModalEventos] = useState([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    dispatch(fetchEvents());
  }, [dispatch]);

  // Convierte cualquier fecha a ISO (YYYY-MM-DD)
  function toISODate(fecha) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return fecha;
    // Si es D/M/YY o D/M/YYYY
    const [d, m, y] = fecha.split('/');
    const year = y.length === 2 ? '20' + y : y;
    return `${year}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // Convierte ISO a formato argentino DD/MM/YYYY
  function toArgDate(iso) {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    return `${day}/${month}/${year}`;
  }

  // Agrupa eventos por fecha (YYYY-MM-DD)
  const eventosPorFecha = eventos.reduce((acc, ev) => {
    const fechaISO = toISODate(ev.fecha);
    acc[fechaISO] = acc[fechaISO] ? [...acc[fechaISO], ev] : [ev];
    return acc;
  }, {});

  // Próximos 3 eventos (ordenados por fecha y hora)
  const proximosEventos = [...eventos]
    .filter(ev => new Date(toISODate(ev.fecha) + 'T' + ev.horaInicio) >= new Date())
    .sort((a, b) =>
      new Date(toISODate(a.fecha) + 'T' + a.horaInicio) - new Date(toISODate(b.fecha) + 'T' + b.horaInicio)
    )
    .slice(0, 3);

  // Renderiza los eventos en cada celda del calendario
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const fecha = date.toISOString().split('T')[0];
      const eventosDelDia = eventosPorFecha[fecha];
      
      if (eventosDelDia && eventosDelDia.length > 0) {
        if (eventosDelDia.length === 1) {
          return (
            <div className={styles.eventsList}>
              <div className={styles.singleEvent}>
                {eventosDelDia[0].titulo}
              </div>
            </div>
          );
        } else {
          return (
            <div className={styles.eventsList}>
              <div className={styles.multipleEventsIndicator}>
                +{eventosDelDia.length}
              </div>
            </div>
          );
        }
      }
    }
    return null;
  };

  // Al hacer click en un día
  const onDayClick = (date) => {
    const fecha = date.toISOString().split('T')[0];
    const eventosDelDia = eventosPorFecha[fecha];
    if (eventosDelDia) {
      setModalEventos(eventosDelDia);
      setShowModal(true);
    }
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const fecha = date.toISOString().split('T')[0];
      if (eventosPorFecha[fecha]) {
        return styles.eventDay; // Esta clase se aplicará al día con evento
      }
    }
    return null;
  };

  return (
    <div className={styles.calendarContainer}>
      <h2 className={styles.title}>Calendario de Eventos</h2>

      {/* Próximos 3 eventos */}
      <div className={styles.upcomingEvents}>
        <h3>Próximos eventos</h3>
        {proximosEventos.length === 0 && <p>No hay eventos próximos.</p>}
        <ul>
          {proximosEventos.map(ev => (
            <li key={ev.id} className={styles.upcomingItem}>
              <span className={styles.upcomingDate}>
                {toArgDate(toISODate(ev.fecha))} {ev.horaInicio}
              </span>
              <span className={styles.upcomingTitle}>{ev.titulo}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Calendario */}
      <div className={styles.calendarWrapper}>
        <Calendar
          onChange={setValue}
          value={value}
          tileContent={tileContent}
          tileClassName={tileClassName}
          onClickDay={onDayClick}
          locale="es-AR"
        />
      </div>

      {/* Modal para mostrar eventos múltiples */}
      {showModal && (
        <div className={styles.multiEventModal} onClick={() => setShowModal(false)}>
          <div className={styles.multiEventContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.multiEventHeader}>
              <h3 className={styles.multiEventTitle}>
                Eventos del día
              </h3>
              <button 
                className={styles.closeMultiEventModal}
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>
            <ul className={styles.multiEventList}>
              {modalEventos.map(evento => (
                <li key={evento.id} className={styles.multiEventItem}>
                  <div className={styles.multiEventItemTitle}>
                    {evento.titulo}
                  </div>
                  <div className={styles.multiEventItemTime}>
                    📅 {toArgDate(toISODate(evento.fecha))}
                    {evento.horaInicio && ` - ⏰ ${evento.horaInicio}`}
                    {evento.horaFin && ` a ${evento.horaFin}`}
                  </div>
                  {evento.descripcion && (
                    <div className={styles.multiEventItemDescription}>
                      {evento.descripcion}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;